// g3-telemetry widget — pure browser ESM, imports only ./capture.js. Mounts
// a shadow-DOM "Report bug" overlay that survives a white-screen crash of
// the host app (own shadow root, `all: initial`, no dependency on the app's
// framework or CSS). Plan 06-02 built idle button + a minimal panel
// (description, send, status). Plan 06-05 adds the element picker (Task 1),
// privacy masking of the screenshot and picked snippets (Task 2), and the
// remaining panel states — whole-page, name, disabled hint, retry, keyboard
// (Task 3).

import {
  startCapture,
  buildReport,
  sendReport,
  collectEnv,
  elementSnippet,
  isMasked,
  MASK_TEXT,
  NAME_STORAGE_KEY,
} from "./capture.js";

const HOST_ID = "g3-telemetry-host";
const PICK_EVENTS = ["click", "pointerdown", "pointerup", "mousedown", "mouseup", "submit", "auxclick"];
const PICKED_OUTLINE = "2px solid #161616";
const HOVER_OUTLINE = "2px solid #161616";

function widgetCss() {
  return `
    * {
      box-sizing: border-box;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }
    .g3-btn {
      position: fixed;
      bottom: 16px;
      right: 16px;
      background: #161616;
      color: #ffffff;
      border: none;
      border-radius: 4px;
      padding: 8px 16px;
      font-size: 14px;
      font-weight: 600;
      line-height: 1.4;
      cursor: pointer;
    }
    .g3-btn:focus-visible { outline: 2px solid #ffffff; outline-offset: 2px; }
    .g3-panel {
      position: fixed;
      bottom: 16px;
      right: 16px;
      width: 320px;
      max-width: calc(100vw - 32px);
      max-height: 80vh;
      overflow-y: auto;
      background: #ffffff;
      border-radius: 4px;
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.25);
      padding: 16px;
      color: #161616;
    }
    .g3-panel h2 {
      font-size: 16px;
      font-weight: 600;
      line-height: 1.2;
      margin: 0 0 16px;
      padding-right: 24px;
    }
    .g3-close {
      position: absolute;
      top: 12px;
      right: 12px;
      background: transparent;
      border: none;
      font-size: 16px;
      line-height: 1;
      width: 32px;
      height: 32px;
      cursor: pointer;
      color: #161616;
    }
    .g3-close:focus-visible { outline: 2px solid #161616; outline-offset: 2px; }
    label {
      display: block;
      font-size: 14px;
      font-weight: 600;
      line-height: 1.4;
      margin-bottom: 8px;
    }
    textarea {
      width: 100%;
      min-height: 80px;
      background: #f4f4f4;
      border: 1px solid #f4f4f4;
      border-radius: 4px;
      padding: 8px;
      font-size: 14px;
      line-height: 1.4;
      color: #161616;
      resize: vertical;
    }
    textarea:focus-visible { outline: 2px solid #161616; outline-offset: 2px; }
    .g3-send {
      margin-top: 16px;
      width: 100%;
      background: #161616;
      color: #ffffff;
      border: none;
      border-radius: 4px;
      padding: 8px 16px;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
    }
    .g3-send:disabled { opacity: 0.5; cursor: not-allowed; }
    .g3-send:focus-visible { outline: 2px solid #161616; outline-offset: 2px; }
    .g3-status { margin-top: 8px; font-size: 14px; line-height: 1.4; }
    .g3-status.g3-error { color: #da1e28; }
    .g3-instruction { font-size: 14px; line-height: 1.4; margin: 0 0 8px; color: #161616; }
    .g3-pick-btn {
      display: block;
      width: 100%;
      margin-bottom: 16px;
      background: #f4f4f4;
      color: #161616;
      border: none;
      border-radius: 4px;
      padding: 8px 16px;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
    }
    .g3-pick-btn:disabled { opacity: 0.5; cursor: not-allowed; }
    .g3-pick-btn:focus-visible { outline: 2px solid #161616; outline-offset: 2px; }
    .g3-pick-banner {
      position: fixed;
      bottom: 16px;
      right: 16px;
      left: 16px;
      max-width: 320px;
      margin-left: auto;
      background: #161616;
      color: #ffffff;
      border-radius: 4px;
      padding: 16px;
      font-size: 14px;
      line-height: 1.4;
    }
    .g3-pick-banner span { display: block; margin-bottom: 8px; }
    .g3-pick-done {
      min-height: 32px;
      background: #ffffff;
      color: #161616;
      border: none;
      border-radius: 4px;
      padding: 8px 16px;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
    }
    .g3-pick-done:focus-visible { outline: 2px solid #ffffff; outline-offset: 2px; }
    .g3-chips { margin-bottom: 16px; }
    .g3-chip { background: #f4f4f4; border-radius: 4px; padding: 8px; margin-bottom: 8px; }
    .g3-chip-head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      margin-bottom: 8px;
    }
    .g3-chip-selector {
      font-family: ui-monospace, SFMono-Regular, "SF Mono", Consolas, monospace;
      font-size: 12px;
      line-height: 1.3;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      flex: 1;
    }
    .g3-remove-note {
      background: transparent;
      border: none;
      font-size: 14px;
      line-height: 1;
      width: 24px;
      height: 24px;
      cursor: pointer;
      color: #161616;
      flex-shrink: 0;
    }
    .g3-remove-note:focus-visible { outline: 2px solid #161616; outline-offset: 2px; }
    .g3-note {
      width: 100%;
      min-height: 48px;
      background: #ffffff;
      border: 1px solid #ffffff;
      border-radius: 4px;
      padding: 8px;
      font-size: 14px;
      line-height: 1.4;
      color: #161616;
      resize: vertical;
    }
    .g3-note:focus-visible { outline: 2px solid #161616; outline-offset: 2px; }
    .g3-checkbox-label {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 14px;
      font-weight: 400;
      line-height: 1.4;
      margin-bottom: 16px;
      cursor: pointer;
    }
    .g3-checkbox-label input[type="checkbox"]:focus-visible { outline: 2px solid #161616; outline-offset: 2px; }
    .g3-hint-text { font-weight: 400; color: #525252; }
    .g3-hint { font-size: 12px; line-height: 1.3; color: #525252; margin: 4px 0 8px; }
    #g3-name {
      display: block;
      width: 100%;
      background: #f4f4f4;
      border: 1px solid #f4f4f4;
      border-radius: 4px;
      padding: 8px;
      font-size: 14px;
      line-height: 1.4;
      color: #161616;
      margin-bottom: 8px;
    }
    #g3-name:focus-visible { outline: 2px solid #161616; outline-offset: 2px; }
    .g3-badge {
      position: fixed;
      min-width: 16px;
      height: 16px;
      padding: 0 4px;
      border-radius: 8px;
      background: #161616;
      color: #ffffff;
      font-size: 12px;
      line-height: 16px;
      text-align: center;
      pointer-events: none;
      z-index: 999999999;
    }
  `;
}

export function installG3Telemetry(opts = {}) {
  const {
    record,
    domToPng,
    ingestUrl,
    reportToken,
    commit,
    deployUrl,
    traceOrigins = [],
    getUser = async () => null,
  } = opts;

  if (!ingestUrl || !reportToken) {
    return { enabled: false, reportError() {} };
  }

  const capture = startCapture({ win: window, record, traceOrigins });

  let host;
  let shadow;
  let panelEl = null;
  let bannerEl = null;
  let badgesEl = null;
  let pickModeActive = false;
  let hoverEl = null;
  const picks = []; // { el, note }
  const outlineMap = new Map(); // el -> original inline outline value

  function isPicked(el) {
    return picks.some((p) => p.el === el);
  }

  function saveOutline(el) {
    if (!outlineMap.has(el)) outlineMap.set(el, el.style.outline || "");
  }

  function restoreOutline(el) {
    if (outlineMap.has(el)) {
      el.style.outline = outlineMap.get(el);
      outlineMap.delete(el);
    }
  }

  function restoreAllOutlines() {
    for (const [el, prev] of outlineMap) {
      try {
        el.style.outline = prev;
      } catch {
        // element may be detached — best-effort
      }
    }
    outlineMap.clear();
  }

  // Badges are position:fixed at the picked element's viewport rect, so a
  // scroll or resize moves the element out from under them -- re-place them
  // once per frame while any exist (06.1 gap: badges stuck on screen).
  let badgeFrame = 0;
  function repositionBadges() {
    if (!badgesEl || badgeFrame) return;
    badgeFrame = requestAnimationFrame(() => {
      badgeFrame = 0;
      renderBadges();
    });
  }

  function renderBadges() {
    if (!badgesEl) {
      badgesEl = document.createElement("div");
      shadow.appendChild(badgesEl);
      window.addEventListener("scroll", repositionBadges, true);
      window.addEventListener("resize", repositionBadges);
    }
    badgesEl.innerHTML = "";
    picks.forEach((pick, i) => {
      let rect;
      try {
        rect = pick.el.getBoundingClientRect();
      } catch {
        return;
      }
      const badge = document.createElement("div");
      badge.className = "g3-badge";
      badge.textContent = String(i + 1);
      badge.style.left = `${Math.round(rect.left - 4)}px`;
      badge.style.top = `${Math.round(rect.top - 4)}px`;
      badgesEl.appendChild(badge);
    });
  }

  function pickElementAt(el) {
    if (!el || isPicked(el)) return;
    saveOutline(el);
    el.style.outline = PICKED_OUTLINE;
    picks.push({ el, note: "" });
    renderBadges();
  }

  function removePick(index) {
    const [pick] = picks.splice(index, 1);
    if (pick) restoreOutline(pick.el);
    renderBadges();
    renderPanelBody();
  }

  function pickCaptureHandler(event) {
    let path;
    try {
      path = typeof event.composedPath === "function" ? event.composedPath() : [];
    } catch {
      path = [];
    }
    if (path.includes(host)) return;
    event.preventDefault();
    event.stopPropagation();
    if (event.type === "click" && event.pointerType !== "touch") {
      pickElementAt(event.target);
    } else if (event.type === "pointerdown" && event.pointerType === "touch") {
      pickElementAt(event.target);
    }
  }

  function pickHoverHandler(event) {
    if (event.pointerType === "touch") return;
    let path;
    try {
      path = typeof event.composedPath === "function" ? event.composedPath() : [];
    } catch {
      path = [];
    }
    if (path.includes(host)) return;
    const el = event.target;
    if (el === hoverEl) return;
    if (hoverEl && !isPicked(hoverEl)) restoreOutline(hoverEl);
    hoverEl = el;
    if (el && !isPicked(el)) {
      saveOutline(el);
      el.style.outline = HOVER_OUTLINE;
    }
  }

  function attachPickListeners() {
    for (const type of PICK_EVENTS) document.addEventListener(type, pickCaptureHandler, true);
    document.addEventListener("pointermove", pickHoverHandler, true);
  }

  function detachPickListeners() {
    for (const type of PICK_EVENTS) document.removeEventListener(type, pickCaptureHandler, true);
    document.removeEventListener("pointermove", pickHoverHandler, true);
    if (hoverEl && !isPicked(hoverEl)) restoreOutline(hoverEl);
    hoverEl = null;
  }

  function showBanner() {
    if (!bannerEl) {
      bannerEl = document.createElement("div");
      bannerEl.setAttribute("data-g3", "pick-banner");
      bannerEl.className = "g3-pick-banner";
      bannerEl.innerHTML = `
        <span>Click elements to mark them. Press Esc or click Done when finished.</span>
        <button type="button" data-g3="pick-done" class="g3-pick-done">Done</button>
      `;
      bannerEl.querySelector('[data-g3="pick-done"]').addEventListener("click", exitPickMode);
      shadow.appendChild(bannerEl);
    }
    bannerEl.style.display = "";
  }

  function hideBanner() {
    if (bannerEl) bannerEl.style.display = "none";
  }

  function enterPickMode() {
    if (pickModeActive || !panelEl) return;
    pickModeActive = true;
    panelEl.style.display = "none";
    showBanner();
    attachPickListeners();
  }

  function exitPickMode() {
    if (!pickModeActive) return;
    pickModeActive = false;
    detachPickListeners();
    hideBanner();
    if (panelEl) panelEl.style.display = "";
    renderPanelBody();
  }

  function chipsHtml() {
    return picks
      .map(
        (pick, i) => `
      <div class="g3-chip" data-g3="chip" data-index="${i}">
        <div class="g3-chip-head">
          <span>${i + 1}</span>
          <span class="g3-chip-selector" title="${escapeHtml(pick.selector || "")}">${escapeHtml(pick.selector || "")}</span>
          <button type="button" class="g3-remove-note" data-g3="remove-note" aria-label="Remove this note">×</button>
        </div>
        <textarea class="g3-note" data-g3="note" maxlength="5000" placeholder="What's wrong here?"></textarea>
      </div>
    `,
      )
      .join("");
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);
  }

  // Rebuilds the chip list from the picks array (selector snapshotted at
  // pick time via elementSnippet, so a removed/re-inserted host element
  // doesn't change an already-picked chip's identity).
  function renderPanelBody() {
    if (!panelEl) return;
    const chipsContainer = panelEl.querySelector('[data-g3="chips-container"]');
    if (!chipsContainer) return;
    picks.forEach((p) => {
      p.selector = p.selector || elementSnippet(p.el).selector;
    });
    chipsContainer.innerHTML = chipsHtml();
    chipsContainer.querySelectorAll('[data-g3="note"]').forEach((textarea, i) => {
      textarea.value = picks[i].note;
      textarea.addEventListener("input", () => {
        picks[i].note = textarea.value;
        updateSendDisabled();
      });
    });
    chipsContainer.querySelectorAll('[data-g3="remove-note"]').forEach((btn, i) => {
      btn.addEventListener("click", () => removePick(i));
    });
    updateSendDisabled();
  }

  // modern-screenshot's per-node clone hook (confirmed from the installed
  // package's Options type: onCloneEachNode(cloned: Node)) — fires for every
  // cloned node before it's rendered to canvas/SVG. For a cloned password
  // input or any cloned node inside [data-g3-mask], blank its form value and
  // replace its text content with MASK_TEXT, so the screenshot never shows
  // what isMasked() would also strip from the replay and picked snippets.
  function maskClonedNode(cloned) {
    if (!cloned || cloned.nodeType !== 1 || !isMasked(cloned)) return;
    const tag = String(cloned.tagName || "").toLowerCase();
    if (tag === "input" || tag === "textarea" || tag === "select") {
      cloned.value = "";
      if (tag === "input") cloned.setAttribute("value", "");
    } else {
      cloned.textContent = MASK_TEXT;
    }
  }

  // Exposed only inside this module — never part of installG3Telemetry's
  // return value or capture.js's exports.
  function buildScreenshotOptions() {
    return {
      width: window.innerWidth,
      height: window.innerHeight,
      onCloneEachNode: maskClonedNode,
    };
  }

  function updateSendDisabled() {
    if (!panelEl) return;
    const description = panelEl.querySelector('[data-g3="description"]');
    const sendBtn = panelEl.querySelector('[data-g3="send"]');
    const hint = panelEl.querySelector('[data-g3="hint"]');
    if (!description || !sendBtn) return;
    const hasText = description.value.trim().length > 0;
    const hasNote = picks.some((p) => p.note.trim().length > 0);
    const enabled = hasText || hasNote;
    sendBtn.disabled = !enabled;
    if (hint) hint.style.display = enabled ? "none" : "";
  }

  function getFocusableElements(root) {
    return Array.from(root.querySelectorAll("button, input, textarea")).filter((el) => !el.disabled);
  }

  function onKeydown(event) {
    if (event.key === "Escape") {
      if (pickModeActive) {
        event.preventDefault();
        exitPickMode();
        return;
      }
      if (panelEl) {
        event.preventDefault();
        closePanel();
      }
      return;
    }
    if (event.key === "Tab" && panelEl && !pickModeActive) {
      const focusables = getFocusableElements(panelEl);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = shadow.activeElement;
      if (event.shiftKey) {
        if (active === first || !focusables.includes(active)) {
          event.preventDefault();
          last.focus();
        }
      } else if (active === last || !focusables.includes(active)) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  function mount() {
    host = document.createElement("div");
    host.id = HOST_ID;
    host.style.all = "initial";
    host.style.position = "fixed";
    host.style.zIndex = "999999999";
    document.body.appendChild(host);
    shadow = host.attachShadow({ mode: "open" });

    const style = document.createElement("style");
    style.textContent = widgetCss();
    shadow.appendChild(style);

    const button = document.createElement("button");
    button.setAttribute("data-g3", "report-button");
    button.setAttribute("aria-label", "Report a bug");
    button.className = "g3-btn";
    button.textContent = "Report bug";
    button.addEventListener("click", openPanel);
    shadow.appendChild(button);

    document.addEventListener("keydown", onKeydown, true);
  }

  function openPanel() {
    if (panelEl) return;
    panelEl = document.createElement("div");
    panelEl.setAttribute("data-g3", "panel");
    panelEl.className = "g3-panel";
    panelEl.innerHTML = `
      <button data-g3="close" aria-label="Close" class="g3-close">×</button>
      <h2>Report a bug</h2>
      <p class="g3-instruction">Pick an element to point at the problem, or skip straight to describing it.</p>
      <button type="button" data-g3="pick" class="g3-pick-btn">Pick element</button>
      <label class="g3-checkbox-label">
        <input type="checkbox" data-g3="whole-page" />
        This isn't about one element
      </label>
      <div class="g3-chips" data-g3="chips-container"></div>
      <label for="g3-description">Describe the problem</label>
      <textarea id="g3-description" data-g3="description" maxlength="20000" placeholder="What did you expect, and what happened instead?"></textarea>
      <label for="g3-name">Your name <span class="g3-hint-text">(optional — remembered on this device)</span></label>
      <input type="text" id="g3-name" data-g3="name" maxlength="200" />
      <div class="g3-hint" data-g3="hint">Add a description or a note on a picked element before sending.</div>
      <button data-g3="send" class="g3-send" disabled>Send report</button>
      <div data-g3="status" class="g3-status"></div>
    `;
    shadow.appendChild(panelEl);

    const closeBtn = panelEl.querySelector('[data-g3="close"]');
    const pickBtn = panelEl.querySelector('[data-g3="pick"]');
    const wholePageCb = panelEl.querySelector('[data-g3="whole-page"]');
    const description = panelEl.querySelector('[data-g3="description"]');
    const nameInput = panelEl.querySelector('[data-g3="name"]');
    const sendBtn = panelEl.querySelector('[data-g3="send"]');
    const status = panelEl.querySelector('[data-g3="status"]');

    closeBtn.addEventListener("click", closePanel);
    pickBtn.addEventListener("click", enterPickMode);
    wholePageCb.addEventListener("change", () => {
      if (wholePageCb.checked) {
        clearPicks();
        pickBtn.disabled = true;
      } else {
        pickBtn.disabled = false;
      }
    });
    description.addEventListener("input", updateSendDisabled);
    try {
      nameInput.value = localStorage.getItem(NAME_STORAGE_KEY) || "";
    } catch {
      // storage error must never block opening the panel
    }
    sendBtn.addEventListener("click", () => {
      void send(description, sendBtn, status, nameInput);
    });

    renderPanelBody();
    description.focus();
  }

  function clearPicks() {
    if (pickModeActive) exitPickMode();
    for (const pick of picks) restoreOutline(pick.el);
    picks.length = 0;
    renderBadges();
    renderPanelBody();
  }

  function discardDraft() {
    if (pickModeActive) exitPickMode();
    restoreAllOutlines();
    picks.length = 0;
    if (badgesEl) {
      badgesEl.remove();
      badgesEl = null;
      // WR-03: a scroll/resize firing just before teardown can already have
      // scheduled a frame via repositionBadges(); if it isn't cancelled here,
      // that frame still runs after badgesEl is nulled, and renderBadges()
      // unconditionally recreates badgesEl + re-registers these two listeners
      // with nothing left to ever remove them again.
      cancelAnimationFrame(badgeFrame);
      badgeFrame = 0;
      window.removeEventListener("scroll", repositionBadges, true);
      window.removeEventListener("resize", repositionBadges);
    }
  }

  function closePanel() {
    if (panelEl) {
      discardDraft();
      panelEl.remove();
      panelEl = null;
      const reportBtn = shadow.querySelector('[data-g3="report-button"]');
      if (reportBtn) reportBtn.focus();
    }
  }

  function setStatus(status, text, isError) {
    status.textContent = text;
    status.className = isError ? "g3-status g3-error" : "g3-status";
  }

  function renderSent(status) {
    status.textContent = "";
    status.className = "g3-status";
    const msg = document.createElement("span");
    msg.textContent = "Report sent — thank you.";
    const closeBtn = document.createElement("button");
    closeBtn.type = "button";
    closeBtn.textContent = "Close";
    closeBtn.className = "g3-send";
    closeBtn.style.marginTop = "8px";
    closeBtn.addEventListener("click", closePanel);
    status.appendChild(msg);
    status.appendChild(closeBtn);
    closeBtn.focus();
  }

  async function send(description, sendBtn, status, nameInput) {
    const text = description.value.trim();
    const hasNote = picks.some((p) => p.note.trim().length > 0);
    if (!text && !hasNote) return;

    const reporterName = nameInput ? nameInput.value.trim() : "";
    try {
      localStorage.setItem(NAME_STORAGE_KEY, reporterName);
    } catch {
      // storage error must never block sending
    }

    sendBtn.textContent = "Sending…";
    sendBtn.disabled = true;
    sendBtn.style.display = "";
    // Claim focus on a stable, always-focusable in-panel element before
    // setStatus tears down whatever was focused (e.g. a just-clicked
    // Try again button living inside `status`) — sendBtn itself can't hold
    // focus here since it's disabled (WR-02).
    description.focus();
    setStatus(status, "", false);

    let screenshot = null;
    let screenshotFailed = false;
    const previousVisibility = host.style.visibility;
    host.style.visibility = "hidden";
    try {
      const dataUrl = await domToPng(document.body, buildScreenshotOptions());
      // Uses the unwrapped fetch — converting the screenshot data URL to a
      // Blob must never itself become a captured network-buffer entry.
      const blobRes = await capture.originalFetch(dataUrl);
      screenshot = await blobRes.blob();
    } catch {
      screenshot = null;
      screenshotFailed = true;
    } finally {
      host.style.visibility = previousVisibility;
    }

    let user = null;
    try {
      user = await getUser();
    } catch {
      user = null;
    }

    const env = collectEnv(window);
    const replayEvents = capture.replay.events();

    const report = buildReport({
      buffers: capture.buffers,
      text,
      reporterName: reporterName || null,
      pageUrl: window.location.href,
      picks: picks.map((p) => ({ ...elementSnippet(p.el), note: p.note })),
      build: { commit: commit || null, deploy_url: deployUrl || null },
      env,
      user,
      replayDropped: false,
      screenshotFailed,
    });

    const result = await sendReport({
      fetch: capture.originalFetch,
      ingestUrl,
      reportToken,
      report,
      screenshot,
      replayEvents,
    });

    if (result.status === "sent") {
      sendBtn.remove();
      renderSent(status);
      return;
    }

    sendBtn.textContent = "Send report";
    sendBtn.disabled = false;
    if (result.status === "rate-limited") {
      setStatus(status, "Too many reports sent from this app just now — try again in a minute.", true);
    } else {
      // hidden, not removed: renderFailed's Try again re-sends through this same sendBtn reference
      sendBtn.style.display = "none";
      renderFailed(status, description, sendBtn, nameInput);
    }
  }

  function renderFailed(status, description, sendBtn, nameInput) {
    status.textContent = "";
    status.className = "g3-status g3-error";
    const msg = document.createElement("span");
    msg.textContent = "Couldn't send the report — check your connection and try again.";
    const retryBtn = document.createElement("button");
    retryBtn.type = "button";
    retryBtn.setAttribute("data-g3", "retry");
    retryBtn.textContent = "Try again";
    retryBtn.className = "g3-pick-btn";
    retryBtn.style.margin = "8px 0 0";
    retryBtn.addEventListener("click", () => {
      void send(description, sendBtn, status, nameInput);
    });
    status.appendChild(msg);
    status.appendChild(retryBtn);
    retryBtn.focus();
  }

  if (document.body) {
    mount();
  } else {
    document.addEventListener("DOMContentLoaded", mount, { once: true });
  }

  return {
    enabled: true,
    reportError(error, componentStack) {
      capture.recordError(error, componentStack);
    },
  };
}
